import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import moment from 'moment';
import { RootState } from './store';
import { baseUrlApi } from '../../utils/url';

export interface Message {
  id: number;
  text: string;
  dateStart: string; // YYYY-MM-DD
  dateEnd: string;   // YYYY-MM-DD
  recurring: boolean;
  OwnerId: number;
  LeaseId?: number | null;
}

interface MessageState {
  messages: Message[];
  loading: boolean;
  error: string | null;
}

const initialState: MessageState = {
  messages: [],
  loading: false,
  error: null,
};

const headers = (token: string) => ({ Authorization: `bearer ${token}` });

export const fetchMessages = createAsyncThunk<Message[], void, { state: RootState }>(
  'message/fetchAll',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const OwnerId = authentification.user.OwnerId;
      const token   = authentification.user.token;
      const res = await axios.get(`${baseUrlApi}/messages/owner/${OwnerId}`, { headers: headers(token) });
      return res.data;
    } catch (e: any) {
      return rejectWithValue(e.response?.data?.message || 'Erreur chargement messages');
    }
  }
);

export const createMessage = createAsyncThunk<Message, Omit<Message, 'id' | 'OwnerId'>, { state: RootState }>(
  'message/create',
  async (data, { getState, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const token   = authentification.user.token;
      const OwnerId = authentification.user.OwnerId;
      const res = await axios.post(`${baseUrlApi}/messages`, { ...data, OwnerId }, { headers: headers(token) });
      return res.data;
    } catch (e: any) {
      return rejectWithValue(e.response?.data?.message || 'Erreur création');
    }
  }
);

export const updateMessage = createAsyncThunk<Message, Message, { state: RootState }>(
  'message/update',
  async (msg, { getState, rejectWithValue }) => {
    try {
      const token = getState().authentification.user.token;
      await axios.put(`${baseUrlApi}/messages/${msg.id}`, msg, { headers: headers(token) });
      return msg;
    } catch (e: any) {
      return rejectWithValue(e.response?.data?.message || 'Erreur mise à jour');
    }
  }
);

export const deleteMessage = createAsyncThunk<number, number, { state: RootState }>(
  'message/delete',
  async (id, { getState, rejectWithValue }) => {
    try {
      const token = getState().authentification.user.token;
      await axios.delete(`${baseUrlApi}/messages/${id}`, { headers: headers(token) });
      return id;
    } catch (e: any) {
      return rejectWithValue(e.response?.data?.message || 'Erreur suppression');
    }
  }
);

// Vérifie si un message est actif aujourd'hui (tient compte du mode récurrent annuel)
export const isMessageActive = (msg: Message): boolean => {
  const now = moment();
  if (msg.recurring) {
    const startMD = moment(msg.dateStart, 'YYYY-MM-DD').format('MMDD');
    const endMD   = moment(msg.dateEnd,   'YYYY-MM-DD').format('MMDD');
    const todayMD = now.format('MMDD');
    if (startMD <= endMD) return todayMD >= startMD && todayMD <= endMD;
    return todayMD >= startMD || todayMD <= endMD; // plage qui traverse le 31/12
  }
  return now.isBetween(msg.dateStart, msg.dateEnd, 'day', '[]');
};

export const getActiveMessages = (messages: Message[]): Message[] =>
  messages.filter(isMessageActive);

const messageSlice = createSlice({
  name: 'message',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchMessages.pending,   s      => { s.loading = true; s.error = null; })
      .addCase(fetchMessages.fulfilled, (s, a) => { s.loading = false; s.messages = a.payload; })
      .addCase(fetchMessages.rejected,  (s, a) => { s.loading = false; s.error = a.payload as string; })
      .addCase(createMessage.fulfilled, (s, a) => { s.messages.push(a.payload); })
      .addCase(updateMessage.fulfilled, (s, a) => {
        const idx = s.messages.findIndex(m => m.id === a.payload.id);
        if (idx !== -1) s.messages[idx] = a.payload;
      })
      .addCase(deleteMessage.fulfilled, (s, a) => {
        s.messages = s.messages.filter(m => m.id !== a.payload);
      });
  },
});

export default messageSlice.reducer;
